import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../../components/Loading';
import {
  PRICES, hasAccess, isSubscribed, openPortal, startCheckout, trialDaysLeft, type Plan,
} from '../../lib/billing';
import { shortDate } from '../../lib/dates';
import { useClassroom } from '../../state/Classroom';

// The teacher's plan: the free trial, subscribing, and managing a subscription.
// Payment itself happens on Stripe's pages; this one only sends people there.

export default function Billing() {
  const { billing, reloadBilling } = useClassroom();
  const [params] = useSearchParams();
  const justPaid = params.get('done') === '1';
  const [busy, setBusy] = useState<Plan | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Back from Stripe: the webhook may land a moment after the redirect.
  useEffect(() => {
    if (!justPaid) return;
    void reloadBilling();
    const t = window.setTimeout(() => void reloadBilling(), 4000);
    return () => window.clearTimeout(t);
  }, [justPaid, reloadBilling]);

  if (billing === undefined) return <Loading what="your plan" />;

  const go = async (what: Plan | 'portal') => {
    setBusy(what); setError(null);
    try { await (what === 'portal' ? openPortal() : startCheckout(what)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(null); }
  };

  const subscribed = isSubscribed(billing);
  const days = trialDaysLeft(billing);
  const access = hasAccess(billing);

  return (
    <div className="narrow">
      <h1>Your plan</h1>

      {justPaid && !subscribed && (
        <div className="notice good" style={{ marginBottom: 16 }}>Thanks! Confirming your payment with Stripe…</div>
      )}

      <div className="panel" style={{ marginBottom: 20 }}>
        {billing?.comped ? (
          <p style={{ margin: 0 }}><strong>Free account.</strong> Everything is included, with nothing to pay.</p>
        ) : subscribed ? (
          <>
            <p style={{ marginTop: 0 }}>
              <strong>Subscribed</strong>, {billing!.plan === 'yearly' ? `${PRICES.yearly.amount} a year` : `${PRICES.monthly.amount} a month`}.
              {billing!.status === 'trialing' && billing!.current_period_end && <> Your free trial runs until {shortDate(billing!.current_period_end)}; the first charge is then.</>}
              {billing!.status === 'active' && billing!.current_period_end && (billing!.cancel_at_period_end
                ? <> Cancelled: it stays on until {shortDate(billing!.current_period_end)}, then stops.</>
                : <> Renews {shortDate(billing!.current_period_end)}.</>)}
            </p>
            {billing!.status === 'past_due' && (
              <div className="notice bad small" style={{ marginBottom: 12 }}>
                Your last payment didn’t go through. Update your card so your classes keep working.
              </div>
            )}
            <button onClick={() => go('portal')} disabled={!!busy}>
              {busy === 'portal' ? 'Opening…' : 'Manage billing'}
            </button>
            <span className="hint">Change your card or plan, download receipts, or cancel.</span>
          </>
        ) : access ? (
          <p style={{ margin: 0 }}>
            <strong>Free trial:</strong> {days} day{days === 1 ? '' : 's'} left (until {shortDate(billing!.trial_ends_at)}).
            Everything works during the trial. Subscribe any time; you won’t be charged until it ends.
          </p>
        ) : (
          <p style={{ margin: 0 }}>
            <strong>Your free trial has ended.</strong> Your classes, students and their work are all still here, and
            students can keep doing what’s assigned. Subscribe to make new assignments and classes.
          </p>
        )}
      </div>

      {!subscribed && !billing?.comped && (
        <div className="grid-2">
          {(['monthly', 'yearly'] as Plan[]).map(p => (
            <div key={p} className="panel">
              <div className="small muted">{p === 'monthly' ? 'Monthly' : 'Yearly'}</div>
              <div style={{ fontSize: 32, fontWeight: 700, margin: '4px 0' }}>
                {PRICES[p].amount}<span className="small muted" style={{ fontWeight: 400 }}> / {PRICES[p].per}</span>
              </div>
              <p className="small muted" style={{ marginTop: 0 }}>{PRICES[p].note}</p>
              <button className={p === 'yearly' ? 'primary' : ''} onClick={() => go(p)} disabled={!!busy}>
                {busy === p ? 'Opening…' : 'Subscribe'}
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <div className="notice bad small" style={{ marginTop: 14 }}>{error}</div>}

      <p className="small muted" style={{ marginTop: 20 }}>
        One subscription covers all of your classes and students. Payments are handled by Stripe;
        we never see your card.
      </p>
    </div>
  );
}
